import type { BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import type { PayoutMethod } from "@/types";
import { BANK_ACCOUNT_ERROR, isValidBankAccount, normalizeBankAccount } from "@/lib/bank-account";
import type {
  DayAvailability,
  ScheduleRow,
  ServiceFormState,
  StepKey,
} from "./types";

export const DAY_LABELS = [
  "Ponedjeljak",
  "Utorak",
  "Srijeda",
  "Četvrtak",
  "Petak",
  "Subota",
  "Nedjelja",
];

const UI_DAY_TO_DB_DAY = [1, 2, 3, 4, 5, 6, 0] as const;

function toMinutes(t: string): number {
  const parts = t.split(":");
  return Number(parts[0]) * 60 + Number(parts[1]);
}

// Pravilo za jedan dan živi u lib-u, jer ga koriste i postavke i server.
export { validateDaySegments } from "@/lib/availability-segments";

export function daysToScheduleRows(days: DayAvailability[]): ScheduleRow[] {
  const rows: ScheduleRow[] = [];
  for (const d of days) {
    if (!d.enabled) continue;
    for (const seg of d.segments) {
      rows.push({ dayOfWeek: d.dayOfWeek, startTime: seg.startTime, endTime: seg.endTime });
    }
  }
  return rows;
}

export function scheduleRowsToDays(rows: ScheduleRow[]): DayAvailability[] {
  return UI_DAY_TO_DB_DAY.map((dayOfWeek) => {
    const segments = rows
      .filter((r) => r.dayOfWeek === dayOfWeek)
      .map((r) => ({ startTime: r.startTime, endTime: r.endTime }))
      .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
    return { dayOfWeek, enabled: segments.length > 0, segments };
  });
}

const TAX_ID_RE = /^\d{13}$/;

type BillingField = keyof BillingIdentityValue;

export interface BillingIssue {
  field: BillingField;
  message: string;
}

/** Polja koja pripadaju odabranom načinu isplate (druga grana se ne šalje). */
const BILLING_BRANCH_FIELDS: Record<PayoutMethod, BillingField[]> = {
  invoice: ["businessName", "businessAddress", "businessTaxId", "businessVatId"],
  work_contract: ["personalAddress", "personalTaxId"],
};
const BANK_FIELDS: BillingField[] = ["bankName", "bankAccount"];
/** Nije obavezno ni za jedan način isplate, pa brisanje mora stići do servera. */
const CLEARABLE_BILLING_FIELDS = new Set<BillingField>(["businessVatId"]);

const trimmed = (b: BillingIdentityValue, field: BillingField) => b[field]?.trim() ?? "";

/** Client mirror of the server billing rules: every problem, in form order. */
export function billingIssues(payoutMethod: PayoutMethod, b: BillingIdentityValue): BillingIssue[] {
  const issues: BillingIssue[] = [];
  if (payoutMethod === "invoice") {
    if (!trimmed(b, "businessName")) issues.push({ field: "businessName", message: "Unesi naziv firme." });
    if (!trimmed(b, "businessAddress")) issues.push({ field: "businessAddress", message: "Unesi adresu firme." });
    if (!TAX_ID_RE.test(trimmed(b, "businessTaxId"))) {
      issues.push({ field: "businessTaxId", message: "JIB mora imati tačno 13 cifara." });
    }
  } else {
    if (!trimmed(b, "personalAddress")) issues.push({ field: "personalAddress", message: "Unesi adresu stanovanja." });
    if (!TAX_ID_RE.test(trimmed(b, "personalTaxId"))) {
      issues.push({ field: "personalTaxId", message: "JMBG mora imati tačno 13 cifara." });
    }
  }
  // Račun za isplatu se traži za oba načina isplate.
  if (trimmed(b, "bankName").length < 2) issues.push({ field: "bankName", message: "Unesi naziv banke." });
  if (!isValidBankAccount(normalizeBankAccount(b.bankAccount ?? ""))) {
    issues.push({ field: "bankAccount", message: `${BANK_ACCOUNT_ERROR}.` });
  }
  return issues;
}

/** Client mirror of the server billing rules; returns an error message or null. */
export function validateBilling(payoutMethod: PayoutMethod, b: BillingIdentityValue): string | null {
  return billingIssues(payoutMethod, b)[0]?.message ?? null;
}

/**
 * Tijelo zahtjeva za podatke o isplati: samo grana odabranog načina isplate plus
 * račun, trimovano, bez praznih polja. Skrivena grana (npr. JIB kad je odabran
 * ugovor o djelu) se nikad ne šalje.
 */
export function billingPayload(payoutMethod: PayoutMethod, b: BillingIdentityValue): Partial<Record<BillingField, string>> {
  const out: Partial<Record<BillingField, string>> = {};
  for (const field of [...BILLING_BRANCH_FIELDS[payoutMethod], ...BANK_FIELDS]) {
    const value = trimmed(b, field);
    if (value) out[field] = value;
  }
  return out;
}

/**
 * Izmjena u postavkama: šalju se samo izmijenjena polja aktivne grane i računa,
 * da staro (npr. WP-migrirano) polje koje terapeut nije dirao ne obori spremanje.
 * Greška se javlja za izmijenjeno polje koje ne prolazi pravilo, uključujući
 * obavezno polje koje je ispražnjeno. Ispražnjen PDV broj se šalje kao "".
 */
export function billingChanges(
  payoutMethod: PayoutMethod,
  current: BillingIdentityValue,
  saved: BillingIdentityValue,
): { payload: Partial<Record<BillingField, string>>; error: string | null } {
  const fields = [...BILLING_BRANCH_FIELDS[payoutMethod], ...BANK_FIELDS];
  const changed = new Set(fields.filter((f) => trimmed(current, f) !== trimmed(saved, f)));
  const issue = billingIssues(payoutMethod, current).find((i) => changed.has(i.field));
  const payload: Partial<Record<BillingField, string>> = {};
  for (const field of changed) {
    const value = trimmed(current, field);
    if (value || CLEARABLE_BILLING_FIELDS.has(field)) payload[field] = value;
  }
  return { payload, error: issue?.message ?? null };
}

const DAY_SHORT: Record<number, string> = { 1: "Pon", 2: "Uto", 3: "Sri", 4: "Čet", 5: "Pet", 6: "Sub", 0: "Ned" };

/**
 * Readable weekly schedule for the review card: days with identical hours are
 * grouped, e.g. ["Pon, Uto, Sri: 09:00–17:00", "Sub: 10:00–13:00"]. Keeps UI day order.
 */
export function summarizeDays(days: DayAvailability[]): string[] {
  const groups: { hours: string; days: string[] }[] = [];
  for (const d of days) {
    if (!d.enabled || d.segments.length === 0) continue;
    const hours = d.segments.map((s) => `${s.startTime}–${s.endTime}`).join(", ");
    const group = groups.find((g) => g.hours === hours);
    if (group) group.days.push(DAY_SHORT[d.dayOfWeek]!);
    else groups.push({ hours, days: [DAY_SHORT[d.dayOfWeek]!] });
  }
  return groups.map((g) => `${g.days.join(", ")}: ${g.hours}`);
}

/** Default working week used when a therapist has no saved schedule. */
export function defaultDays(): DayAvailability[] {
  return UI_DAY_TO_DB_DAY.map((dayOfWeek, i) => ({
    dayOfWeek,
    enabled: i < 5,
    segments: i < 5 ? [{ startTime: "09:00", endTime: "17:00" }] : [],
  }));
}

interface OnboardingSnapshot {
  scheduleCount: number;
  serviceCount: number;
  /** Bar jedna glavna oblast i jedna populacija (`isMatchingProfileReady`). */
  matchingReady: boolean;
  // Agreement step: contract (active `ugovor_terapeut`) and, for migrated therapists,
  // payout method + platform terms, all on one screen. A part that is not shown
  // counts as accepted, so a hidden part can never block the wizard.
  showContract?: boolean;
  contractAccepted?: boolean;
  showBillingTerms?: boolean;
  termsAccepted?: boolean;
  /** Migrated therapist with imported availability + service: one review step replaces both forms. */
  reviewMode?: boolean;
}

export interface OnboardingGates {
  showAgreement: boolean;
  reviewMode: boolean;
}

export interface OnboardingState {
  completion: Record<StepKey, boolean>;
  landingStep: StepKey;
}

// Base wizard order (new enrollee, no contract configured). `onboardingStepOrder`
// inserts `agreement` after `welcome` and swaps availability + services for `review`.
export const ONBOARDING_STEP_ORDER: StepKey[] = [
  "welcome",
  "availability",
  "services",
  "matching",
  "finish",
];

export function onboardingStepOrder(gates: OnboardingGates): StepKey[] {
  const order: StepKey[] = ["welcome"];
  if (gates.showAgreement) order.push("agreement");
  if (gates.reviewMode) order.push("review");
  else order.push("availability", "services");
  order.push("matching", "finish");
  return order;
}

/**
 * Every step between welcome and finish is required (matching included), so the
 * farthest reachable step is the first incomplete one, or finish when all are done.
 */
function farthestOpenStepIndex(completion: Record<StepKey, boolean>, order: StepKey[]): number {
  const firstOpen = order.findIndex((step) => step !== "welcome" && step !== "finish" && !completion[step]);
  return firstOpen === -1 ? order.indexOf("finish") : firstOpen;
}

export function canOpenOnboardingStep(
  step: StepKey,
  completion: Record<StepKey, boolean>,
  order: StepKey[] = ONBOARDING_STEP_ORDER,
): boolean {
  return order.indexOf(step) <= farthestOpenStepIndex(completion, order);
}

export function clampOnboardingStepIndex(
  index: number,
  completion: Record<StepKey, boolean>,
  order: StepKey[] = ONBOARDING_STEP_ORDER,
): number {
  const bounded = Math.max(0, Math.min(order.length - 1, index));
  return Math.min(bounded, farthestOpenStepIndex(completion, order));
}

/**
 * Next step when advancing forward: always the following step, clamped at the
 * first incomplete step. Pre-filled steps are deliberately *not* skipped: a migrated
 * therapist confirms the imported data on the review step.
 */
export function nextOnboardingStepIndex(
  fromIndex: number,
  completion: Record<StepKey, boolean>,
  order: StepKey[] = ONBOARDING_STEP_ORDER,
): number {
  return clampOnboardingStepIndex(fromIndex + 1, completion, order);
}

export function computeOnboardingState(snap: OnboardingSnapshot): OnboardingState {
  const availability = snap.scheduleCount >= 1;
  const services = snap.serviceCount >= 1;
  const agreement =
    (!snap.showContract || !!snap.contractAccepted) && (!snap.showBillingTerms || !!snap.termsAccepted);
  const completion: Record<StepKey, boolean> = {
    welcome: true,
    agreement,
    review: availability && services,
    availability,
    services,
    matching: snap.matchingReady,
    finish: false,
  };

  let landingStep: StepKey;
  if (!agreement) {
    // Nothing signed yet: start from the top so the welcome screen frames the flow.
    landingStep = "welcome";
  } else if (!snap.reviewMode && !availability) {
    landingStep = snap.scheduleCount === 0 && snap.serviceCount === 0 ? "welcome" : "availability";
  } else if (!snap.reviewMode && !services) {
    landingStep = "services";
  } else {
    // Data steps have content (fresh save or import). Land on welcome so the wizard
    // is walked in order; the step rail still allows jumping ahead.
    landingStep = "welcome";
  }
  return { completion, landingStep };
}

export const EMPTY_SERVICE: ServiceFormState = {
  id: null,
  name: "",
  description: "",
  durationMinutes: 50,
  price: null,
  modality: "online",
  maxMembers: null,
};

// `price` is what the therapist types: the service price WITHOUT VAT (platformBase),
// not their net. With the default money config these give clients 117.00 / 58.50 /
// 175.50 KM, the same client prices the templates had before the field changed meaning.
export const SERVICE_TEMPLATES = {
  individual: {
    name: "Individualna psihoterapija",
    description:
      "Individualne seanse za odrasle — rad na anksioznosti, stresu, životnim promjenama i ličnom rastu. Siguran i povjerljiv prostor prilagođen tvom tempu.",
    durationMinutes: 50,
    price: 100,
    modality: "online" as const,
    maxMembers: null,
  },
  intro: {
    name: "Uvodna seansa",
    description:
      "Kratka prva seansa za upoznavanje — razgovaramo o tome šta te dovodi, tvojim ciljevima i kako bismo radili zajedno. Bez obaveze nastavka.",
    durationMinutes: 30,
    price: 50,
    modality: "online" as const,
    maxMembers: null,
  },
  couples: {
    name: "Terapija za parove",
    description:
      "Seanse za parove — poboljšanje komunikacije, povjerenja i razrješavanje konflikata uz strukturiranu podršku terapeuta.",
    durationMinutes: 90,
    price: 150,
    modality: "both" as const,
    maxMembers: 2,
  },
} as const;

export type ServiceTemplateKey = keyof typeof SERVICE_TEMPLATES | "blank";

export function applyServiceTemplate(key: ServiceTemplateKey): ServiceFormState {
  if (key === "blank") return { ...EMPTY_SERVICE };
  const t = SERVICE_TEMPLATES[key];
  return {
    id: null,
    name: t.name,
    description: t.description,
    durationMinutes: t.durationMinutes,
    price: t.price,
    modality: t.modality,
    maxMembers: t.maxMembers,
  };
}
